import { fromByteArray, toByteArray } from 'base64-js'

export const IVSize = 12

/** Remove all whitespace from a string */
const removeWhitespace = (s: string): string => s.replace(/\s+/g, '')

/** from a password, generate an AES key through PBKDF2 */
export async function deriveAuthKeyFromPassword(
  password: string,
  salt: string,
  iterations: number
): Promise<CryptoKey> {
  const passwordBytes = new TextEncoder().encode(password) // encode pw as UTF-8
  const baseKey = await crypto.subtle.importKey('raw', passwordBytes, 'PBKDF2', false, [
    'deriveKey',
  ])

  const key = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-512', salt: toByteArray(salt), iterations },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['wrapKey', 'unwrapKey']
  )

  return key
}

/** Generate a random new key pair */
export function generateKeyPair(keyUsage: readonly KeyUsage[]): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey(
    {
      name: 'RSA-OAEP',
      hash: { name: 'SHA-256' },
      modulusLength: 4096,
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
    },
    true,
    keyUsage
  )
}

/**
 * Export an RSA public key into a string in JWK encoded form.
 */
export async function exportPublicKey(key: CryptoKey): Promise<JsonWebKey> {
  return await crypto.subtle.exportKey('jwk', key)
}

/**
 * Import a JWK encoded RSA public key
 */
export async function importPublicKeyJWK(
  jwk: JsonWebKey,
  keyUsages: readonly KeyUsage[]
): Promise<CryptoKey> {
  return window.crypto.subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    true,
    keyUsages
  )
}

/**
 * Export an RSA private key into a JWK.
 *
 * When in doubt, use exportPrivateKey with a wrapping key instead.
 */
export async function exportPrivateKeyJWK(key: CryptoKey): Promise<JsonWebKey> {
  return exportPublicKey(key)
}

/**
 * Import a JWK encoded RSA private key, exported with exportPrivateKeyJWK.
 */
export async function importPrivateKeyJWK(
  jwk: JsonWebKey,
  keyUsages: readonly KeyUsage[]
): Promise<CryptoKey> {
  return importPublicKeyJWK(jwk, keyUsages)
}

/** Exports a key pair to a string.
 * The key pair is jwk encoded and encrypted using AES-GCM with a random 12 byte IV prepended.
 */
export async function exportPrivateKey(key: CryptoKey, wrappingKey: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IVSize))
  const wrappedKey: ArrayBuffer = await crypto.subtle.wrapKey('jwk', key, wrappingKey, {
    name: 'AES-GCM',
    iv,
  })

  const wrappedKeyWithIV = new Uint8Array(wrappedKey.byteLength + iv.byteLength)
  wrappedKeyWithIV.set(iv, 0)
  wrappedKeyWithIV.set(new Uint8Array(wrappedKey), iv.byteLength)

  return fromByteArray(wrappedKeyWithIV)
}

/** Imports a private key pair that was exported using exportPrivateKey. */
export async function importPrivateKey(
  wrappedKeyData: string,
  unwrappingKey: CryptoKey,
  keyUsage: readonly KeyUsage[]
) {
  const decodedWrappedKeyWithIV: Uint8Array = toByteArray(removeWhitespace(wrappedKeyData))
  const buffer = decodedWrappedKeyWithIV.buffer
  const iv = new Uint8Array(buffer, 0, IVSize)
  const decodedWrappedKey = new Uint8Array(buffer, IVSize)

  return crypto.subtle.unwrapKey(
    'jwk',
    decodedWrappedKey,
    unwrappingKey,
    { name: 'AES-GCM', iv },
    { name: 'RSA-OAEP', hash: { name: 'SHA-256' } },
    true,
    keyUsage
  )
}

/** Generates a new, random encryption key that can be used to encrypt patient data and documents.
 * This is a 256 bit AES-GCM key.
 */
export function generateEncryptionKey(keyUsage: readonly KeyUsage[]): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, keyUsage)
}

/**
 * Exports a encryption key into a string.
 * The key is an AES key, jwk encoded, and encrypted with a device private key using RSA-OAEP.
 */
export async function exportEncryptionKey(key: CryptoKey, publicKey: CryptoKey): Promise<string> {
  const wrappedKey: ArrayBuffer = await crypto.subtle.wrapKey('jwk', key, publicKey, {
    name: 'RSA-OAEP',
  })

  return fromByteArray(new Uint8Array(wrappedKey))
}

/**
 * Imports an encrypted AES key that was exported with exportEncryptionKey.
 *
 * @samedi/crypto-js/subtle/symmetric uses AES-CBC keys.
 */
export async function importEncryptionKey(
  privateKey: CryptoKey,
  keyDataEncrypted: string,
  keyUsage: readonly KeyUsage[],
  keyType: 'AES-GCM' | 'AES-CBC' | undefined = undefined
): Promise<CryptoKey> {
  const decodedWrappedKey: Uint8Array = toByteArray(removeWhitespace(keyDataEncrypted))

  const unwrapKey = async (algo: RsaOaepParams) =>
    crypto.subtle.unwrapKey(
      'jwk',
      decodedWrappedKey,
      privateKey,
      { name: 'RSA-OAEP' },
      algo,
      true,
      keyUsage
    )

  if (keyType) {
    return await unwrapKey({ name: keyType })
  }

  try {
    return await unwrapKey({ name: 'AES-GCM' })
  } catch (error) {
    return await unwrapKey({ name: 'AES-CBC' })
  }
}
